import { Command, Paperclip, Send, CornerDownLeft, X } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { SlashCommandPicker } from './SlashCommandPicker';

import type { SlashCommandItem } from '../hooks/use-slash-commands';

const TEXTAREA_MAX_HEIGHT = 120;
const MAX_FILES = 10;

export interface ChatInputHandle {
  setValue: (value: string) => void;
  focus: () => void;
}

export interface ChatInputProps {
  onSend: (message: string, files?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  slashCommands?: SlashCommandItem[];
  slashCommandStatus?: 'idle' | 'loading' | 'success' | 'error';
  onSlashCommandsFetch?: () => void;
  onSlashCommandsRefresh?: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Scan backward from cursor to find the nearest unmatched `/` that starts
 * a slash command token. Returns the position of the `/`, or -1 if none found.
 */
function findSlashStart(text: string, cursorPos: number): number {
  for (let i = cursorPos - 1; i >= 0; i--) {
    if (text[i] === ' ' || text[i] === '\n') return -1;
    if (text[i] === '/') return i;
  }
  return -1;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({
  onSend,
  disabled,
  placeholder = 'Message...',
  className,
  slashCommands = [],
  slashCommandStatus = 'idle',
  onSlashCommandsFetch,
  onSlashCommandsRefresh,
}, ref) {
  const [value, setValue] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slashFilter, setSlashFilter] = useState('');
  const [slashStartPos, setSlashStartPos] = useState<number | null>(null);
  const hasFetchedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  useImperativeHandle(ref, () => ({
    setValue: (v: string) => {
      setValue(v);
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
        }
      });
    },
    focus: () => textareaRef.current?.focus(),
  }));

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
    }
  }, [value]);

  const filteredCommands = slashCommands.filter((c) => c.name.toLowerCase().startsWith(slashFilter.toLowerCase()));

  // Clamp selectedIndex when filtered list changes
  useEffect(() => {
    if (filteredCommands.length === 0) {
      setSelectedIndex(0);
    } else if (selectedIndex >= filteredCommands.length) {
      setSelectedIndex(filteredCommands.length - 1);
    }
  }, [filteredCommands.length, selectedIndex]);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setSlashFilter('');
    setSlashStartPos(null);
    setSelectedIndex(0);
  }, []);

  const selectCommand = useCallback(
    (command: SlashCommandItem) => {
      if (slashStartPos === null) return;
      const cursorPos = textareaRef.current?.selectionStart ?? value.length;
      const before = value.slice(0, slashStartPos);
      const after = value.slice(cursorPos);
      const newValue = `${before}/${command.name} ${after}`;
      setValue(newValue);
      closePicker();

      const newCursorPos = slashStartPos + command.name.length + 2; // `/` + name + ` `
      requestAnimationFrame(() => {
        textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current?.focus();
      });
    },
    [slashStartPos, value, closePicker],
  );

  const addFiles = useCallback((files: FileList | File[]) => {
    const newFiles = Array.from(files);
    if (newFiles.length === 0) return;
    setPendingFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
      const unique = newFiles.filter((f) => !existing.has(`${f.name}-${f.size}-${f.lastModified}`));
      const remaining = MAX_FILES - prev.length;
      return [...prev, ...unique.slice(0, Math.max(0, remaining))];
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed && pendingFiles.length === 0) return;
    onSend(trimmed, pendingFiles.length > 0 ? pendingFiles : undefined);
    setValue('');
    setPendingFiles([]);
    closePicker();
  }, [value, pendingFiles, onSend, closePicker]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart ?? newValue.length;
      setValue(newValue);

      // Trigger lazy fetch on first keystroke
      if (!hasFetchedRef.current && slashCommandStatus === 'idle' && onSlashCommandsFetch) {
        hasFetchedRef.current = true;
        onSlashCommandsFetch();
      }

      // Detect slash command context
      const slashPos = findSlashStart(newValue, cursorPos);
      if (slashPos >= 0) {
        const filter = newValue.slice(slashPos + 1, cursorPos);
        if (!pickerOpen) {
          setPickerOpen(true);
          setSlashStartPos(slashPos);
        }
        setSlashFilter(filter);
        setSelectedIndex(0);
      } else if (pickerOpen) {
        closePicker();
      }
    },
    [pickerOpen, slashCommandStatus, onSlashCommandsFetch, closePicker],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (disabled) return;

    if (pickerOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (filteredCommands.length > 0 ? (prev + 1) % filteredCommands.length : 0));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          filteredCommands.length > 0 ? (prev - 1 + filteredCommands.length) % filteredCommands.length : 0,
        );
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        if (filteredCommands.length > 0) {
          e.preventDefault();
          selectCommand(filteredCommands[selectedIndex]);
          return;
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          return;
        }
        // Enter with no filtered commands — close picker and send
        closePicker();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closePicker();
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const canSend = (value.trim().length > 0 || pendingFiles.length > 0) && !disabled;
  const isEmpty = value.length === 0 && pendingFiles.length === 0;

  return (
    <div
      className={`relative flex flex-col gap-2 transition-colors duration-200${disabled ? ' opacity-50 cursor-not-allowed' : ''}${className ? ` ${className}` : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[16px] border-2 border-dashed border-sd-warning bg-sd-warning/10 pointer-events-none">
          <span className="text-sd-sm font-medium text-sd-warning">Drop files to attach</span>
        </div>
      )}

      {/* Pending file chips — above the card */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-1">
          {pendingFiles.map((file, index) => (
            <span
              key={`${file.name}-${file.size}-${index}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-sd-xs rounded-sd-sm bg-sd-surface-elevated text-sd-text border border-sd-border max-w-[200px]"
            >
              <Paperclip size={12} className="shrink-0 text-sd-text-muted" />
              <span className="truncate">{file.name}</span>
              <span className="shrink-0 text-sd-text-muted">({formatFileSize(file.size)})</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="shrink-0 ml-0.5 rounded-full p-0.5 hover:bg-sd-border transition-colors"
                aria-label={`Remove ${file.name}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Card + picker anchor — picker must be outside overflow-hidden card */}
      <div className="relative">
        {/* Slash command picker — positioned above the card */}
        {pickerOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-1 z-20">
            <SlashCommandPicker
              commands={filteredCommands}
              status={slashCommandStatus}
              selectedIndex={selectedIndex}
              onSelect={selectCommand}
              onRefresh={() => onSlashCommandsRefresh?.()}
              onHover={setSelectedIndex}
            />
          </div>
        )}

        {/* Wraparound card container */}
        <div
          data-testid="chat-input-card"
          className="relative bg-sd-surface border border-sd-border/40 rounded-[16px] shadow-[0_2px_12px_rgba(0,0,0,0.06),0_0_0_1px_rgba(0,0,0,0.02)] overflow-hidden transition-all duration-200 focus-within:ring-2 focus-within:ring-sd-warning/30 focus-within:border-sd-warning/50"
        >
          {/* Input row */}
          <div className="flex items-end gap-0 px-sd-2 py-sd-1">
            {/* Attach button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="flex items-center justify-center self-end min-h-[44px] min-w-[44px] rounded-sd-lg text-sd-text-muted hover:text-sd-warning hover:bg-sd-warning/10 transition-colors duration-150 shrink-0"
              aria-label="Attach files"
            >
              <Paperclip size={18} />
            </button>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              aria-label="Upload file attachments"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  addFiles(e.target.files);
                  e.target.value = '';
                }
              }}
            />

            <textarea
              ref={textareaRef}
              aria-label="Chat message"
              role={pickerOpen ? 'combobox' : undefined}
              aria-expanded={pickerOpen || undefined}
              aria-controls={pickerOpen ? 'slash-command-listbox' : undefined}
              aria-activedescendant={pickerOpen && filteredCommands.length > 0 ? `slash-cmd-${filteredCommands[selectedIndex]?.name}` : undefined}
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className="resize-none border-0 bg-transparent text-sd-text text-sd-sm w-full px-sd-2 py-sd-2 max-h-[120px] overflow-y-auto placeholder:text-sd-text-muted/60 placeholder:text-sd-sm focus-visible:outline-none focus-visible:ring-0 transition-none"
            />

            {canSend && (
              <button
                type="button"
                className="flex items-center justify-center self-end bg-sd-warning text-white min-h-[44px] min-w-[44px] rounded-sd-lg transition-all duration-150 active:scale-95 shadow-sd-sm hover:shadow-sd-md shadow-[0_0_8px_rgba(217,119,6,0.3)] shrink-0"
                aria-label="Send message"
                onClick={handleSend}
              >
                <Send size={18} />
              </button>
            )}
          </div>

          {/* Capability hints — inside the card, below the input row */}
          {isEmpty && !disabled && !pickerOpen && (
            <div
              className="flex items-center gap-sd-2 px-sd-3 py-sd-1 border-t border-sd-border/15 overflow-x-auto"
              aria-hidden="true"
            >
              <div
                className="inline-flex items-center gap-1 px-sd-2 py-[3px] rounded-sd-full bg-sd-surface-elevated/60 border border-sd-border/30 text-[11px] text-sd-text-muted whitespace-nowrap select-none cursor-pointer hover:border-sd-warning/40 hover:text-sd-text-secondary transition-colors duration-150"
                style={{ animation: 'sd-reveal-up 300ms cubic-bezier(0.16, 1, 0.3, 1) both 50ms' }}
                onClick={() => {
                  textareaRef.current?.focus();
                  const syntheticEvent = { target: { value: '/', selectionStart: 1 } } as React.ChangeEvent<HTMLTextAreaElement>;
                  setValue('/');
                  handleChange(syntheticEvent);
                }}
              >
                <Command size={10} className="text-sd-warning/70" />
                <span>
                  Type <span className="font-mono font-semibold text-sd-warning/70">/</span> for commands
                </span>
              </div>
              <div
                className="inline-flex items-center gap-1 px-sd-2 py-[3px] rounded-sd-full bg-sd-surface-elevated/60 border border-sd-border/30 text-[11px] text-sd-text-muted whitespace-nowrap select-none cursor-pointer hover:border-sd-warning/40 hover:text-sd-text-secondary transition-colors duration-150"
                style={{ animation: 'sd-reveal-up 300ms cubic-bezier(0.16, 1, 0.3, 1) both 100ms' }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={10} className="text-sd-text-muted/60" />
                <span>Attach files</span>
              </div>
              <div
                className="inline-flex items-center gap-1 px-sd-2 py-[3px] rounded-sd-full bg-sd-surface-elevated/60 border border-sd-border/30 text-[11px] text-sd-text-muted whitespace-nowrap select-none"
                style={{ animation: 'sd-reveal-up 300ms cubic-bezier(0.16, 1, 0.3, 1) both 150ms' }}
              >
                <CornerDownLeft size={10} className="text-sd-text-muted/60" />
                <span>
                  <kbd className="font-mono text-[10px]">Shift+Enter</kbd> new line
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
